import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";

const tiers = [
  {
    name: "Starter",
    price: "$199 — $299",
    target: "Best for landing pages, brochure sites & MVPs.",
    features: [
      "5–8 page responsive website",
      "Mobile-first design",
      "Basic SEO setup",
      "Contact form integration",
      "1 month email support",
    ],
  },
  {
    name: "Professional",
    price: "$499 — $899",
    target: "Custom apps, e-commerce & growing businesses.",
    highlight: true,
    features: [
      "Custom web application",
      "Backend + database",
      "Advanced SEO + analytics",
      "Payment gateway",
      "3 months priority support",
      "Performance optimization",
    ],
  },
  {
    name: "Enterprise",
    price: "$1,199 — $3,499+",
    target: "AI products, SaaS, large-scale platforms.",
    features: [
      "Full-custom SaaS / platform",
      "AI chatbot + integrations",
      "E-commerce + payments",
      "Dedicated account manager",
      "Advanced analytics + dashboards",
      "12 months support",
    ],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-32 lg:py-44 px-6 lg:px-10 border-t border-nova-line overflow-hidden">
      <div className="orb orb-cyan w-[400px] h-[400px] top-20 -left-32 opacity-20" />
      <div className="orb orb-gold w-[500px] h-[500px] bottom-0 -right-40 opacity-25" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="mono-label mb-4">[ 03 — Investment ]</div>
            <h2 className="font-display font-bold tracking-tighter text-4xl lg:text-6xl text-nova-text leading-[0.95]">
              Transparent pricing.<br />
              <span className="text-nova-mute">No hidden fees.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-nova-mute text-lg leading-relaxed self-end">
            Pick a starting point. Every project is then tailored — final pricing depends on
            scope, complexity and integrations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              className={`relative border p-10 flex flex-col ${
                t.highlight
                  ? "border-nova-gold/40 bg-nova-surface shadow-[inset_0_0_60px_rgba(134,239,172,0.06)]"
                  : "border-nova-line bg-nova-surface/40"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-10 inline-flex items-center gap-1.5 px-3 py-1 bg-nova-gold text-nova-bg text-xs font-mono uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}
              <div className="mono-label mb-6">{`Tier 0${i + 1}`}</div>
              <h3 className="font-display font-bold text-3xl text-nova-text mb-1">{t.name}</h3>
              <p className="text-nova-mute text-sm mb-8">{t.target}</p>
              <div className="mb-10">
                <div className="font-display font-bold text-4xl lg:text-5xl text-nova-text tracking-tight">
                  {t.price}
                </div>
                <div className="mono-label mt-2 text-nova-mute">Project range</div>
              </div>
              <ul className="space-y-3 mb-10 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-nova-text/90 text-sm">
                    <Check className="w-4 h-4 text-nova-gold mt-0.5 flex-shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={`mailto:novacreationscodes@gmail.com?subject=${encodeURIComponent(`New Project Inquiry — ${t.name} Tier`)}&body=${encodeURIComponent(`Hi,\n\nI'm interested in the ${t.name} tier (${t.price} USD).\n\nName:\nCompany:\nPhone:\nProject description:\nTimeline:\n\nThanks!`)}`}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`group inline-flex items-center justify-between gap-2 px-6 py-4 border transition-all duration-300 ${
                  t.highlight
                    ? "bg-nova-gold text-nova-bg border-nova-gold hover:bg-[#C9F9D6]"
                    : "border-white/15 text-nova-text hover:border-nova-gold hover:text-nova-gold"
                }`}
              >
                <span>Choose {t.name}</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
